import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { MediaImg } from "./MediaImg";
import { VideoPlayer } from "./VideoPlayer";

export const CaseStudyModal = ({ project, onClose }) => {
  const gallery = project.gallery || [];
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (lightbox !== null) setLightbox(null);
        else onClose();
      }
      if (lightbox !== null && gallery.length > 0) {
        if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % gallery.length);
        if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, gallery.length, onClose]);

  return (
    <motion.div
      data-testid="case-study-modal"
      className="fixed inset-0 z-[95] flex items-end justify-center p-0 sm:items-center sm:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-night/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto bg-sand shadow-2xl"
      >
        <div className="relative flex items-center justify-center bg-night">
          {project.video ? (
            <VideoPlayer
              src={project.video}
              poster={project.poster}
              className="max-h-[62vh] w-full object-contain"
              testId="case-study-video"
            />
          ) : (
            <MediaImg
              src={project.image}
              alt={project.title}
              className="max-h-[62vh] w-full object-contain"
            />
          )}
          <button
            data-testid="case-study-close"
            onClick={onClose}
            aria-label="Close case study"
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center bg-night/60 text-sand backdrop-blur-sm transition-colors duration-300 hover:bg-gold hover:text-ink"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-7 sm:p-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold-dark">
            {project.category} · {project.location} · {project.year}
          </p>
          <h3 className="mt-3 font-display text-3xl font-bold tracking-tight text-ink">{project.title}</h3>
          <div className="mt-5 flex flex-wrap gap-2">
            {project.metrics.map((m) => (
              <span key={m} className="bg-gold-light px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-gold-dark">
                {m}
              </span>
            ))}
          </div>
          <div className="mt-8 space-y-7">
            {[
              ["The Challenge", project.challenge],
              ["Our Approach", project.approach],
              ["The Outcome", project.outcome],
            ].map(([label, copy]) => (
              <div key={label}>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40">{label}</p>
                <p className="mt-2 leading-relaxed text-smoke">{copy}</p>
              </div>
            ))}
          </div>

          {gallery.length > 0 && (
            <div className="mt-10" data-testid="case-study-gallery">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40">Full Gallery</p>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {gallery.map((src, i) => (
                  <button
                    key={src}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setLightbox(i)}
                    aria-label={`Open photo ${i + 1} of ${gallery.length}`}
                    className="group relative overflow-hidden"
                  >
                    <MediaImg
                      src={src}
                      alt={`${project.title} — photo ${i + 1}`}
                      className="h-36 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-44"
                    />
                    <span className="absolute inset-0 bg-night/0 transition-colors duration-300 group-hover:bg-night/20" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-9 flex flex-wrap items-center gap-4">
            {project.link && (
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-study-site-link"
                className="inline-block border border-ink/20 px-6 py-3.5 font-display text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-gold hover:text-gold-dark"
              >
                Visit the site
              </a>
            )}
            <a
              href="/start-a-project"
              data-testid="case-study-cta"
              className="inline-block bg-gold px-6 py-3.5 font-display text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ink hover:text-sand"
            >
              Start a project like this
            </a>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {lightbox !== null && gallery[lightbox] && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-night/95 p-4 sm:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <motion.img
              key={gallery[lightbox]}
              src={gallery[lightbox]}
              alt={`${project.title} — photo ${lightbox + 1}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[85vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
              data-testid="gallery-lightbox-image"
            />
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Close gallery"
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center bg-sand/10 text-sand backdrop-blur-sm transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              <X size={20} />
            </button>
            <button
              data-testid="gallery-lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
              }}
              aria-label="Previous photo"
              className="absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-sand/10 text-sand backdrop-blur-sm transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="gallery-lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i + 1) % gallery.length);
              }}
              aria-label="Next photo"
              className="absolute right-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-sand/10 text-sand backdrop-blur-sm transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              <ChevronRight size={22} />
            </button>
            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-xs tracking-[0.3em] text-sand/60">
              {String(lightbox + 1).padStart(2, "0")} / {String(gallery.length).padStart(2, "0")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
